import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ServiceService, {
  ICreateServiceParams,
} from '@clickersphere/core/services/ServiceService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { ServiceQueryData, SERVICES_QUERY_KEY } from './useServices';

export function useCreateService() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { isPending: createServiceIsPending, mutateAsync: createServiceAsync } =
    useMutation({
      mutationFn: async (data: ICreateServiceParams) => {
        const service = await ServiceService.create(data);
        return service as ServiceQueryData;
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });

        const tempId = String(crypto.randomUUID());
        const now = new Date().toISOString();

        queryClient.setQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
          (old) => [
            ...(old ?? []).concat({
              ...variables,
              id: tempId,
              tenantId: '',
              createdAt: now,
              updatedAt: now,
              queryStatus: 'pending',
            }),
          ],
        );

        return { tempId };
      },
      onSuccess: async (data, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });

        queryClient.setQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
          (old) =>
            old?.map((item) => (item.id === context.tempId ? data : item)),
        );

        toast(t('catalog.toasts.serviceCreated'), 'success');
      },
      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });

        queryClient.setQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
          (old) => old?.filter((item) => item.id !== context?.tempId),
        );

        toast(t('catalog.toasts.serviceCreateError'), 'error');
      },
    });

  return {
    createServiceAsync,
    createServiceIsPending,
  };
}
